import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AdminRequestError,
  fetchCallerIdSettings,
  syncPhoneNumbersFromTwilio,
  updateCallerIdSettings,
  type CallerIdSettings,
  type CallerIdSettingsResponse,
} from './api';

// =====================================================================
// Buying stats + the live tuning knobs.
//
// Every value here used to require a DEPLOY to change — the wrong cost for numbers
// whose entire purpose is to be tuned against live traffic. The region weight is the
// clearest case: it ships at 4 so local presence wins outright, but TNS treats
// area-code matching as a bad-actor signal, so whether that costs us answer rate is a
// real question the Performance tab can now settle — without a redeploy to test it.
//
// Two things this screen must be honest about:
//   1. COUNTS, NOT DOLLARS. We don't know Twilio's per-number rental price, so we show
//      what we know. An invented dollar figure would get budgeted against.
//   2. `effective` vs `settings`. Application env overrides the row, so an edit can
//      save and change nothing. When they disagree, say so loudly.
// =====================================================================

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-muted-foreground text-[0.65rem] tracking-wide uppercase">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-muted-foreground text-[0.65rem]">{hint}</div>}
    </div>
  );
}

function Row({
  label,
  hint,
  example,
  children,
  overridden,
}: {
  label: string;
  hint?: string;
  // A concrete "if you set X, then Y happens". The hints explain the REASONING
  // behind a knob, which is not the same as knowing what a number does — someone
  // about to type 0.3 into a weight needs to picture the outcome, not the theory.
  example?: string;
  children: React.ReactNode;
  overridden?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
      <div className="min-w-0">
        <div className="text-xs font-medium">{label}</div>
        {hint && <div className="text-muted-foreground text-[0.65rem]">{hint}</div>}
        {example && (
          <div className="text-muted-foreground mt-0.5 text-[0.65rem] italic">{example}</div>
        )}
        {overridden && (
          // The row saved fine and does nothing. Silence here would be a trap.
          <div className="text-[0.65rem] font-medium text-amber-700">
            overridden by deploy config — editing this has no effect
          </div>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Toggle({
  on,
  onChange,
  disabled,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={cn(
        'rounded-full px-2 py-0.5 text-[0.65rem] font-medium',
        on ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground',
        disabled && 'opacity-50',
      )}
    >
      {on ? 'on' : 'off'}
    </button>
  );
}

function NumField({
  value,
  onChange,
  step,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: string;
  disabled?: boolean;
}) {
  // `Number('')` is 0, so an empty box must not reach the draft — clearing one to
  // retype it would otherwise save a real 0 into a field where 0 is out of range.
  return (
    <input
      type="number"
      min="0"
      step={step ?? '1'}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const parsed = Number(e.target.value);
        if (e.target.value !== '' && Number.isFinite(parsed)) onChange(parsed);
      }}
      className="border-input w-20 rounded border px-1 py-0.5 text-right text-xs tabular-nums"
    />
  );
}

// A comma-separated list. The raw text is held here rather than re-derived from the
// parsed array: parsing drops the empty entry a lone "," makes, so a value rendered from
// the array deletes the separator on the keystroke that types it and the codes run
// together. Local text, parsed array on every change, resynced when the draft moves
// underneath (a save, a reload, a discard).
function ListField({
  value,
  onChange,
  clean,
}: {
  value: string[] | undefined;
  onChange: (v: string[]) => void;
  clean: (s: string) => string;
}) {
  const joined = (value ?? []).join(', ');
  const [text, setText] = useState(joined);
  const [lastJoined, setLastJoined] = useState(joined);

  if (joined !== lastJoined) {
    setLastJoined(joined);
    setText(joined);
  }

  return (
    <input
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = e.target.value
          .split(',')
          .map((s) => clean(s.trim()))
          .filter(Boolean);
        setLastJoined(parsed.join(', '));
        onChange(parsed);
      }}
      className="border-input w-36 rounded border px-1 py-0.5 text-xs"
    />
  );
}

// A cap, where empty is a real value meaning "no cap" and 0 is a budget of zero.
// Kept separate from NumField rather than adding a flag, because the two differ in
// what they can hold, not just in how they behave.
function CapField({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <input
      type="number"
      min="0"
      step="1"
      placeholder="none"
      value={value ?? ''}
      onChange={(e) => {
        if (e.target.value === '') return onChange(null);
        const parsed = Number(e.target.value);
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      className="border-input w-20 rounded border px-1 py-0.5 text-right text-xs tabular-nums"
    />
  );
}

// The four weight names, used by BOTH the formula and the rows below it.
//
// One constant, not two lists: the formula said "area-code match / needs a chance /
// recently used" while the settings said "Region match / Exploration / Wear" — the same
// four things in two vocabularies, so the formula couldn't be read against the fields it
// describes. Renaming a weight now renames it in both places by construction.
const WEIGHT_LABEL = {
  region: 'Region match',
  health: 'Health',
  exploration: 'Exploration',
  wear: 'Recent use',
} as const;

// The Evidence setting names. Referenced from the Health / Exploration hints, so an
// operator reading "shaped by X" can find X below without translating.
const EVIDENCE_LABEL = {
  minSample: 'Calls needed to judge a number',
  shortCall: 'Short call',
  probation: 'Probation window',
} as const;

// The scoring formula, rendered with the operator's OWN live weights.
//
// Without this the weights are unusable: "0.5" means nothing until you know it's a
// multiplier on a 0–1 factor, that the four are summed, that wear is SUBTRACTED, and
// that the highest total wins. Mirrors Selector.explain/2 exactly — if that changes,
// this must change with it, or the screen teaches the wrong model.
function WeightsFormula({ draft }: { draft: CallerIdSettings }) {
  const n = (v: number) => (Number.isFinite(v) ? String(v) : '0');

  return (
    <div className="bg-muted/40 mb-2 rounded-md border p-2">
      <div className="text-[0.65rem] leading-relaxed">For every call, each number scores:</div>
      <div className="mt-1 font-mono text-[0.65rem] leading-relaxed break-words">
        score = <b>{n(draft.weight_region)}</b> × {WEIGHT_LABEL.region}
        {'  +  '}
        <b>{n(draft.weight_health)}</b> × {WEIGHT_LABEL.health}
        <br />
        {'          '}+ <b>{n(draft.weight_exploration)}</b> × {WEIGHT_LABEL.exploration}
        {'  −  '}
        <b>{n(draft.weight_wear)}</b> × {WEIGHT_LABEL.wear}
      </div>
      <ul className="text-muted-foreground mt-1.5 space-y-0.5 text-[0.65rem]">
        <li>• The number with the highest score gets the call.</li>
        <li>
          • Each factor is 0 to 1, so a weight is just how much that factor matters. Doubling a
          weight doubles its say.
        </li>
        <li>
          • <b>{WEIGHT_LABEL.wear} is subtracted</b> — the more a number has been used this hour,
          the lower it scores, so the next call goes to a fresher one.
        </li>
        <li>
          • {WEIGHT_LABEL.region} is 1 for the same state, then 0.66 / 0.33 / 0 as it gets further
          away.
        </li>
        <li>
          • Set any weight to 0 and that factor is ignored entirely. Only the relative sizes matter,
          not the absolute numbers.
        </li>
      </ul>
    </div>
  );
}

export function NumbersSettings() {
  const [data, setData] = useState<CallerIdSettingsResponse | null>(null);
  const [draft, setDraft] = useState<CallerIdSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchCallerIdSettings();
      setData(res);
      setDraft(res.settings);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load settings');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Lived on the removed /fleet page; moved here rather than lost — this is the
  // only UI that imports numbers bought or ported directly in Twilio into
  // `phone_numbers`, and without it the inventory silently drifts stale.
  // No confirmation: the sync lists inventory, never buys, and re-running is a
  // no-op. Reloads the stats on success so "Owned" reflects the import.
  const syncFromTwilio = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    setErr(null);
    try {
      const r = await syncPhoneNumbersFromTwilio();
      setSyncResult(
        `Fetched ${r.fetched} from Twilio · added ${r.created} · already known ${r.skipped}` +
          // Only when non-zero: a permanent 0 trains people to stop reading the line.
          (r.failed ? ` · could not import ${r.failed}` : ''),
      );
      await load();
    } catch (e) {
      setErr(e instanceof AdminRequestError ? e.message : 'Failed to sync numbers from Twilio');
    } finally {
      setSyncing(false);
    }
  }, [load]);

  const set = <K extends keyof CallerIdSettings>(key: K, value: CallerIdSettings[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setSaved(false);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setErr(null);
    try {
      // `draft` holds the whole row, so PATCHing it verbatim would write back the
      // switches this screen never renders — a stale tab silently rolling one back.
      const { caller_id_engine: _engine, rotate_recent_caller_ids: _rotate, ...rendered } = draft;
      const res = await updateCallerIdSettings(rendered);
      setData((d) => (d ? { ...d, settings: res.settings, effective: res.effective } : d));
      setDraft(res.settings);
      setSaved(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!data || !draft) {
    return (
      <div className="text-muted-foreground p-4 text-center text-xs">
        {err ?? 'Loading settings…'}
      </div>
    );
  }

  const { stats, effective } = data;
  // A field whose live value doesn't match the row is being overridden by deploy
  // config — the edit would save and do nothing.
  const overridden = (k: keyof CallerIdSettings) =>
    JSON.stringify(effective[k]) !== JSON.stringify(data.settings[k]);

  return (
    <div className="flex flex-col gap-4">
      {err && (
        <div className="bg-destructive/10 text-destructive rounded-md p-2 text-xs">{err}</div>
      )}

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold">Numbers bought</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void syncFromTwilio()}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from Twilio'}
          </Button>
        </div>
        {syncResult && <p className="text-muted-foreground mb-2 text-[0.65rem]">{syncResult}</p>}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat
            label="Today"
            value={String(stats.bought_today)}
            hint={`${stats.remaining_today} left of ${stats.caps.per_day}`}
          />
          <Stat label="Last 7 days" value={String(stats.bought_7d)} />
          <Stat label="Last 30 days" value={String(stats.bought_30d)} />
          <Stat
            label="Owned"
            value={String(stats.total_numbers)}
            hint={`${stats.remaining_total} below the ${stats.caps.total} ceiling`}
          />
        </div>
        <p className="text-muted-foreground mt-1 text-[0.65rem]">
          Counts only numbers we bought — synced and hand-added numbers cost nothing new. Rental
          cost in dollars isn&rsquo;t shown because nothing fetches Twilio&rsquo;s per-number price;
          a made-up figure would be worse than none.
        </p>
      </section>

      {stats.recent.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold">Recently bought</h3>
          <div className="max-h-40 overflow-y-auto rounded-md border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-2 py-1 font-medium">Number</th>
                  <th className="px-2 py-1 font-medium">Region</th>
                  <th className="px-2 py-1 font-medium">Status</th>
                  <th className="px-2 py-1 font-medium">Bought</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((n) => (
                  <tr key={n.id} className="border-t">
                    <td className="px-2 py-1 font-mono">{n.number}</td>
                    <td className="text-muted-foreground px-2 py-1">{n.region ?? '—'}</td>
                    <td className="text-muted-foreground px-2 py-1">{n.status}</td>
                    <td className="text-muted-foreground px-2 py-1">
                      {new Date(n.purchased_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-1 text-xs font-semibold">Buying</h3>
        <Row
          label="Buy numbers automatically"
          hint="On import, for lead regions no number covers. US/CA only — Twilio needs paperwork for EU. Ships OFF: turning this on is the decision to let the dialer spend without asking, bounded by the two caps below and the ceiling."
          example="Example: you import 200 New York leads and own no New York number → it buys one (~$1.15/month, recurring; releasing is manual). Leave it off and those leads are still called, just from a number in another area — so they don't see a local number."
          overridden={overridden('auto_provision_enabled')}
        >
          <Toggle
            on={draft.auto_provision_enabled}
            onChange={(v) => set('auto_provision_enabled', v)}
          />
        </Row>
        <Row
          label="Max per import"
          hint="One import can never provision a fleet."
          example="Example: at 5, a list missing numbers for 8 states buys only 5 — the five states with the most leads. The other 3 are still called, using a number from another area."
          overridden={overridden('auto_provision_max_per_run')}
        >
          <NumField
            value={draft.auto_provision_max_per_run}
            onChange={(v) => set('auto_provision_max_per_run', v)}
          />
        </Row>
        <Row
          label="Max per day"
          hint="Bounds a loop firing on every import."
          example="Example: at 10, the 11th purchase today is refused and written to the log. Imports keep working — they just call from a number in another area instead."
          overridden={overridden('auto_provision_max_per_day')}
        >
          <NumField
            value={draft.auto_provision_max_per_day}
            onChange={(v) => set('auto_provision_max_per_day', v)}
          />
        </Row>
        <Row
          label="Max numbers owned"
          hint="Absolute backstop if the day counter is wrong."
          example="Example: at 200, nothing more is bought once you own 200 live numbers. Release retired ones to make room."
          overridden={overridden('auto_provision_max_total')}
        >
          <NumField
            value={draft.auto_provision_max_total}
            onChange={(v) => set('auto_provision_max_total', v)}
          />
        </Row>
        <Row
          label="Buy in countries"
          hint="ISO-2 codes. Twilio won't sell EU numbers over the API."
          example="Example: US, CA. Adding GB will not help — Twilio refuses to sell UK numbers without address paperwork, so UK leads keep being called from a number we already own."
          overridden={overridden('auto_provision_countries')}
        >
          <ListField
            value={draft.auto_provision_countries}
            onChange={(v) => set('auto_provision_countries', v)}
            clean={(s) => s.toUpperCase()}
          />
        </Row>
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold">Dialing</h3>
        <Row
          label="Countries we hold no local number in"
          hint="Calling codes, bare and without the +. Empty dials everywhere. A pickup-rate control, not a compliance one: a foreign caller ID rarely gets answered, so these are not worth dialling until we own numbers there."
          example="Example: 49, 39 stops every German and Italian lead being dialled — they stay in the pool, counted as undialable rather than silently skipped. Remove a code once we own numbers there. Nothing stops you entering 1 or 44, which would halt most of the fleet."
          overridden={overridden('no_local_caller_id_calling_codes')}
        >
          <ListField
            value={draft.no_local_caller_id_calling_codes}
            onChange={(v) => set('no_local_caller_id_calling_codes', v)}
            clean={(s) => s.replace(/^\+/, '')}
          />
        </Row>
        <Row
          label="Enforce the hourly cap"
          hint="Can THROTTLE a busy pool: ~5 mature numbers cap out near 450 dials/hour, then the pool waits for the next hour."
          example="Example: a number allowed 90 calls/hour stops at 90 and returns next hour. If every number in a pool is capped, that pool pauses until the hour rolls."
          overridden={overridden('enforce_hourly_cap')}
        >
          <Toggle on={draft.enforce_hourly_cap} onChange={(v) => set('enforce_hourly_cap', v)} />
        </Row>
        {/* Shown disabled rather than removed: an operator who knows this row would
            read its absence as a UI bug, and a live toggle that saves without changing
            anything is worse than an honest dead one. */}
        <Row
          label="Daily hard cap per number"
          hint="An absolute ceiling on one number's calls in a day, never exceeded — a reputation control rather than pacing, so it applies even with the hourly cap above turned off. Per number, but the effect is fleet-wide: multiply by how many numbers you own to get the day's total."
          example="Example: at 60 with 400 numbers you can place 24,000 calls a day. Set it to 20 on the same fleet and everything stops once 8,000 are placed. The day is a UTC day, so a halted pool comes back early evening UK time, mid-shift for US dialing — and one US working day can span two budgets. Leave it empty — no ceiling — until someone has counted the live fleet. Setting it to 0 stops every number dialing."
          overridden={overridden('daily_hard_cap')}
        >
          <CapField value={draft.daily_hard_cap} onChange={(v) => set('daily_hard_cap', v)} />
        </Row>
        <Row
          label="Daily fair share per number"
          // Keyed on `effective`, not the row: deploy config outranks the row, so the
          // row can name an engine that is not the one reading this value.
          hint={
            effective.caller_id_engine === 'hierarchy'
              ? 'A target, not a limit. A number over it stands aside for the next-closest one that still has share, and is only pushed past it once every number in the lead’s own country is also spent.'
              : `Not read by the engine currently running (${effective.caller_id_engine}) — editing this changes nothing today. Only the closest-first engine uses it, and which engine picks is set through the API, not here.`
          }
          example="Example: at 20, a number that has already made 20 calls today stands aside for one that hasn’t, even if that one sits a little further from the lead. Once every in-country number is at 20 it keeps using them rather than reach abroad. Empty turns the preference off; 0 puts every number over its share, so each pick counts as a deliberate breach."
          overridden={overridden('daily_soft_cap')}
        >
          <CapField value={draft.daily_soft_cap} onChange={(v) => set('daily_soft_cap', v)} />
        </Row>
        <Row
          label="Use the health selector"
          hint="Superseded, and shown disabled rather than removed so the row is not silently missing. Which engine picks is now set through the API; this value only still resolves rows written before that setting existed."
          example="Example: nothing you do here changes which engine runs — that is why it does not accept input."
          overridden={overridden('use_health_selector')}
        >
          <Toggle
            disabled
            on={draft.use_health_selector}
            onChange={(v) => set('use_health_selector', v)}
          />
        </Row>
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold">Selector weights</h3>
        <WeightsFormula draft={draft} />
        <Row
          label={WEIGHT_LABEL.region}
          hint="Defaults to 4, which makes region win outright — a closer number always beats a further one, and the other weights only rank numbers within the same region tier."
          example="Example: at 0 the dialer ignores area codes entirely. Below 3.6 the other weights can outrank region, so a foreign number can take a local lead."
          overridden={overridden('weight_region')}
        >
          <NumField
            step="0.1"
            value={draft.weight_region}
            onChange={(v) => set('weight_region', v)}
          />
        </Row>
        <Row
          label={WEIGHT_LABEL.health}
          hint={`How much a number's answer/engagement history counts. What that history means is set under Evidence below: "${EVIDENCE_LABEL.shortCall}" decides which calls count as hang-ups, and "${EVIDENCE_LABEL.minSample}" decides when we trust a number's own record instead of its peers' average.`}
          example="Example: raise it and a proven number from another state beats a local one nobody answers. At 0, health is ignored."
          overridden={overridden('weight_health')}
        >
          <NumField
            step="0.1"
            value={draft.weight_health}
            onChange={(v) => set('weight_health', v)}
          />
        </Row>
        <Row
          label={WEIGHT_LABEL.exploration}
          hint={`Gives quiet numbers a turn so they can prove themselves. At 0 the system stops learning. The bonus starts at 1 for a number with no calls and fades to 0 as it reaches "${EVIDENCE_LABEL.minSample}" under Evidence below.`}
          example="Example: at 0, a number you just bought may never get tried — so it never earns a score, and never gets used. Leave it above 0."
          overridden={overridden('weight_exploration')}
        >
          <NumField
            step="0.1"
            value={draft.weight_exploration}
            onChange={(v) => set('weight_exploration', v)}
          />
        </Row>
        <Row
          label={WEIGHT_LABEL.wear}
          hint="How much of its hour a number has already used up: 0 if it hasn't been called from this hour, 1 once it has hit its hourly allowance. Subtracted, so the dialer keeps reaching for numbers it hasn't been leaning on."
          example="Example: a number allowed 90 calls an hour that has made 45 scores 0.5 here — half used up. Raise this and calls spread evenly across your numbers; at 0, one number can take every call in a pool. (This works even with the hourly cap off — there the allowance just paces things rather than stopping them.)"
          overridden={overridden('weight_wear')}
        >
          <NumField step="0.1" value={draft.weight_wear} onChange={(v) => set('weight_wear', v)} />
        </Row>
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold">Evidence</h3>
        {/* These are NOT terms in the formula, which is exactly why they confuse: the
            weights above multiply four factors, and these settings define what two of
            those factors MEAN before any multiplying happens. Saying so here is cheaper
            than leaving everyone to work it out — and one of the three doesn't touch
            scoring at all, which nothing else on the screen reveals. */}
        <div className="bg-muted/40 mb-2 rounded-md border p-2">
          <div className="text-[0.65rem] leading-relaxed">
            These aren&rsquo;t part of the score. They decide what two of its factors <i>mean</i>:
          </div>
          <ul className="text-muted-foreground mt-1 space-y-0.5 text-[0.65rem]">
            <li>
              • <b>{EVIDENCE_LABEL.minSample}</b> shapes both <b>{WEIGHT_LABEL.health}</b> and{' '}
              <b>{WEIGHT_LABEL.exploration}</b>. A number with 0 dials scores its peers&rsquo;
              average health (not zero) and gets the full {WEIGHT_LABEL.exploration} bonus; once it
              reaches this many dials it&rsquo;s judged on its own record and the bonus is gone.
            </li>
            <li>
              • <b>{EVIDENCE_LABEL.shortCall}</b> shapes <b>{WEIGHT_LABEL.health}</b> — hang-ups are
              what pull a number&rsquo;s health down.
            </li>
            <li>
              • <b>{EVIDENCE_LABEL.probation}</b> affects <b>nothing in the score</b>. It only
              decides whether a newly-bought number gets flagged for you to review.
            </li>
          </ul>
        </div>
        <Row
          label={EVIDENCE_LABEL.minSample}
          hint="How many calls a number must make before we believe what its numbers say about it. Like a batting average: someone who bats twice and misses twice isn't a bad batter — you just don't know yet."
          example='Example: at 30, a number that has made only 12 calls does three things at once — the dashboard shows "not enough data" instead of a red 0%, the dialer scores it as probably-typical-like-its-neighbours rather than judging it on 12 calls, and it gets extra turns so it can reach 30 and prove itself. At 5 you would be condemning numbers on almost nothing.'
          overridden={overridden('min_sample')}
        >
          <NumField value={draft.min_sample} onChange={(v) => set('min_sample', v)} />
        </Row>
        <Row
          label={`${EVIDENCE_LABEL.shortCall} (seconds)`}
          hint="Answered and ended under this counts as a hang-up, which lowers the number's health. The default 15s is Hiya's published unwanted-call indicator — the one threshold here that comes from an outside source rather than our judgement."
          example="Example: at 15, a 12-second call counts against the number and a 20-second one doesn't. This is the ONLY way call length affects anything — a 2-second and a 14-second call are treated identically."
          overridden={overridden('short_call_seconds')}
        >
          <NumField
            value={draft.short_call_seconds}
            onChange={(v) => set('short_call_seconds', v)}
          />
        </Row>
        <Row
          label={`${EVIDENCE_LABEL.probation} (days)`}
          hint="How long a newly-bought number is watched."
          example="Example: at 14, a number bought today is flagged for review if it answers far worse than its neighbours within two weeks."
          overridden={overridden('probation_days')}
        >
          <NumField value={draft.probation_days} onChange={(v) => set('probation_days', v)} />
        </Row>
      </section>

      <div className="flex items-center gap-2">
        <button
          onClick={() => void save()}
          disabled={saving}
          className="bg-foreground text-background rounded-md px-3 py-1 text-xs font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && (
          <span className="text-xs text-emerald-700">Saved — applies to the next call.</span>
        )}
      </div>
    </div>
  );
}
